/**
  * @module msysXLSX
  * @description
  * Module for creating Excel .xlsx files
  *
  * Example of use:
  * const sheets = [...]
  * const xls = { sheets }
  * const jszip_options = { type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
  * const blob = await mxlsx.compose(xls, jszip_options)
  */

const JSZip = require('jszip')

const mxlsx = module.exports = {}

const FORMATS = {
  header: 1,
  red: 2,
  green: 3,
  blue: 4,
  orange: 5,
  brown: 6,
  violet: 7,
  magenta: 8,
  cyan: 9,
  silver: 10,
  gray: 11,
  steel: 12,
  bold: 13,
}

const PARTS = {
  '_rels/.rels': `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="workbook.xml"/>
</Relationships>`,

  '_rels/workbook.xml.rels': `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId0" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,

  '[Content_Types].xml': `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`,

  'styles.xml': `<?xml version="1.0" encoding="UTF-8"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts>
    <font><name val="Calibri"/><sz val="12"/><color theme="1"/></font>
    <font><name val="Calibri"/><sz val="12"/><color theme="1"/><b/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FFCC0000"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FF009900"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FF0000FF"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FFCC7700"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FF884444"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FF7030A0"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FFFF40FF"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FF00CCFF"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FFD6D6D6"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FF929292"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FF797979"/></font>
    <font><name val="Calibri"/><sz val="12"/><color rgb="FFFFFFFF"/></font>
  </fonts>
  <fills>
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="00EEEEEE"/></patternFill></fill>
  </fills>
  <borders>
    <border></border>
    <border><bottom style="medium"></bottom></border>
  </borders>
  <cellXfs>
    <xf/>
    <xf applyFont="1" fontId="1" applyFill="1" fillId="2" applyBorder="1" borderId="1" applyAlignment="1"><alignment horizontal="center" /></xf>
    <xf applyFont="1" fontId="2"/>
    <xf applyFont="1" fontId="3"/>
    <xf applyFont="1" fontId="4"/>
    <xf applyFont="1" fontId="5"/>
    <xf applyFont="1" fontId="6"/>
    <xf applyFont="1" fontId="7"/>
    <xf applyFont="1" fontId="8"/>
    <xf applyFont="1" fontId="9"/>
    <xf applyFont="1" fontId="10"/>
    <xf applyFont="1" fontId="11"/>
    <xf applyFont="1" fontId="12"/>
    <xf applyFont="1" fontId="1"/>
  </cellXfs>
</styleSheet>`,

  'workbook.xml': `<?xml version="1.0" encoding="UTF-8"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <bookViews><workbookView/></bookViews>
  <sheets>
  </sheets>
</workbook>`,
}

const wb_rel_sheet = '  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="sheet1.xml"/>\n'
const ct_sheet = '  <Override PartName="/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>\n'
const wb_sheet = '    <sheet name="$sheetname" sheetId="1" r:id="rId1"/>\n'
const sheet_template = `<?xml version="1.0" encoding="UTF-8"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetViews>
    <sheetView tabSelected="1" workbookViewId="0">
      <pane xSplit="$freezex" ySplit="$freezey" topLeftCell="$freezecell" state="frozen"/>
    </sheetView>
  </sheetViews>
  <sheetData></sheetData>
</worksheet>`

mxlsx.compose = async function(xls, jszip_options = { type: 'base64' }) {
  const sheets = xls.sheets || []
  const parts = Object.assign({}, PARTS)

  for(let sheet_idx=1; sheet_idx<=sheets.length; sheet_idx++) {
    const sheet = sheets[sheet_idx-1]

    // add sheet to Workbook relations
    parts['_rels/workbook.xml.rels'] = parts['_rels/workbook.xml.rels'].replace('</Relationships>', wb_rel_sheet.replace(/1/g, sheet_idx)+'</Relationships>')

    // add sheets to Content Types
    parts['[Content_Types].xml'] = parts['[Content_Types].xml'].replace('</Types>', ct_sheet.replace(/1/g, sheet_idx)+'</Types>')

    // add sheets to Workbook
    parts['workbook.xml'] = parts['workbook.xml'].replace('  </sheets>', wb_sheet.replace(/1/g, sheet_idx).replace('$sheetname', sheet.name)+'  </sheets>')

    let txt = sheet_template

    // setup freeze panes
    const freezeX = sheet.freezeX>0 ? sheet.freezeX : 0
    const freezeY = sheet.freezeY>0 ? sheet.freezeY : 0
    const freezeCell = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.substr(freezeX, 1) + (freezeY+1)
    txt = txt.replace(/\$freezex/g, freezeX).replace(/\$freezey/g, freezeY).replace(/\$freezecell/g, freezeCell)

    // setup column widths
    if(Array.isArray(sheet.cols)) {
      const cols = '\n  <cols>' + sheet.cols.map((col, col_idx)=>`<col min="${col_idx+1}" max="${col_idx+1}" width="${col}" customWidth="1"/>`).join('') + '</cols>'
      txt = txt.replace('</sheetViews>', '</sheetViews>'+cols)
    }

    let data = ''
    sheet.rows.forEach(row=>{
      data += '<row>'
      row.forEach(cell=>{
        let [value, format] = Array.isArray(cell) ? cell : [cell]
        data += '<c'
        format = FORMATS[format]
        if(format) data += ` s="${format}"`
        if(typeof (value)==='number') {
          data += '><v>'+value+'</v>'
        }else{
          data += ' t="inlineStr"><is><t>'+value+'</t></is>'
        }
        data += '</c>'
      })
      data += '</row>'
    })
    parts['sheet'+(sheet_idx)+'.xml'] = txt.replace('<sheetData>', '<sheetData>'+data)
  }

  const zip = new JSZip()

  for(const path in parts) {
    zip.file(path, parts[path])
  }

  return zip.generateAsync(jszip_options)
} // compose
